import {FetchBaseQueryError} from '@reduxjs/toolkit/dist/query';
import {QueryReturnValue} from '@reduxjs/toolkit/dist/query/baseQueryTypes';

import {userJobDispatcher} from 'app/jobs/userJobDispatcher';
import {sweetAPI} from 'app/store/sweet-api';
import {
  providesTagsByURL,
  showLoadingBar,
  showMessages,
} from 'app/store/sweet-api/utils';
import {
  KB_API_URL,
  KB_API_URL_COPY,
  KB_ARTICLES_API_URL,
  KB_BLOCKS_API_URL,
  KB_CATEGORIES_API_URL,
  KB_THEMES_API_URL,
} from 'app/urls';
import {DEFAULT_PAGE_SIZE} from 'app/utils/constants';
import {gt, ngt} from 'app/utils/i18n';
import {
  ARTICLES_TAG,
  BLOCKS_TAG,
  CATEGORY_TAG,
  KB_TAG,
  THEMES_TAG,
} from 'app/utils/rtk-tags';

import type {
  ArticleLocationParamsType,
  BulkAddArticlesParams,
  BulkDeleteArticlesParams,
  CreateNewArticleProps,
  CreateNewCategoryProps,
  KBCategoriesLocationParamsType,
  KBFetchParamsType,
  KBLocationParamsType,
  KBThemeFetchParamsType,
  KBThemeLocationParamsType,
  UpdateCategoryProps,
  UpdateKnowledgeBaseProps,
} from './types';
import type {
  KBArticleSerializedType,
  KBBlockSerializedType,
  KBBlockWriteType,
  KBCategorySerializedType,
  KBThemeSerializedType,
  KnowledgebaseSerializedType,
  ListResponseType,
  UserJobSerializedType,
} from 'sweet-types';

export const extendedAPI = sweetAPI
  .enhanceEndpoints({
    addTagTypes: [KB_TAG, CATEGORY_TAG, ARTICLES_TAG, THEMES_TAG, BLOCKS_TAG],
  })
  .injectEndpoints({
    endpoints: (build) => ({
      knowledgeBases: build.query<
        ListResponseType<KnowledgebaseSerializedType>,
        KBLocationParamsType
      >({
        query: (params = {}) => ({
          url: KB_API_URL,
          params: {page: '1', ...params},
        }),
        providesTags: providesTagsByURL(KB_TAG),
        onQueryStarted: (args, api) => {
          showLoadingBar(args, api);
          showMessages({
            onError: () => gt('Unable to load the knowledge bases.'),
          })(args, api);
        },
      }),
      knowledgeBase: build.query<KnowledgebaseSerializedType, {kb_id: string}>({
        query: ({kb_id}) => ({
          url: `${KB_API_URL}${kb_id}/`,
        }),
        providesTags: (response) =>
          response != null
            ? [
                {type: KB_TAG, id: response.url},
                {type: KB_TAG, id: 'list'},
              ]
            : [],
        onQueryStarted: (args, api) => {
          showLoadingBar(args, api);
          showMessages({
            onError: () => gt('Unable to load the knowledge base.'),
          })(args, api);
        },
      }),
      updateKnowledgeBase: build.mutation<
        KnowledgebaseSerializedType,
        UpdateKnowledgeBaseProps
      >({
        query: ({kb_id, ...body}) => ({
          url: `${KB_API_URL}${kb_id}/`,
          method: 'PATCH',
          body,
        }),
        invalidatesTags: (response) =>
          response != null
            ? [
                {type: KB_TAG, id: 'list'},
                {type: KB_TAG, id: response.url},
              ]
            : [],
      }),
      deleteKnowledgeBase: build.mutation<unknown, string>({
        query: (url) => ({url, method: 'DELETE'}),
        onQueryStarted: showMessages({
          onError: () =>
            gt("Couldn't move the knowledge base to the recycle bin"),
          onSuccess: () => gt('Knowledge base moved to recycle bin'),
        }),
        invalidatesTags: () => [{type: KB_TAG, id: 'list'}],
      }),
      categories: build.query<
        ListResponseType<KBCategorySerializedType>,
        KBCategoriesLocationParamsType
      >({
        query: (params = {}) => ({
          url: KB_CATEGORIES_API_URL,
          params: {page: '1', ...params},
        }),
        providesTags: providesTagsByURL(CATEGORY_TAG),
        onQueryStarted: (args, api) => {
          showLoadingBar(args, api);
          showMessages({
            onError: () => gt('Unable to load categories.'),
          })(args, api);
        },
      }),
      addCategory: build.mutation<unknown, CreateNewCategoryProps>({
        query: (body) => ({
          url: KB_CATEGORIES_API_URL,
          method: 'POST',
          body,
        }),
        invalidatesTags: () => [{type: CATEGORY_TAG, id: 'list'}],
        onQueryStarted: (args, api) => {
          showLoadingBar(args, api);
          showMessages({
            onError: () => gt('Unable to create a category.'),
            onSuccess: () => gt('Category was created successfully.'),
          })(args, api);
        },
      }),
      updateCategory: build.mutation<
        KBCategorySerializedType,
        UpdateCategoryProps
      >({
        query: ({url, ...body}) => ({
          url,
          body,
          method: 'PATCH',
        }),
        invalidatesTags: (category) => [
          {
            type: CATEGORY_TAG,
            id: category?.url ?? '',
          },
        ],
        onQueryStarted: (args, api) => {
          showLoadingBar(args, api);
          showMessages({
            onError: () => gt('Unable to update the Knowledge Base Category.'),
            onSuccess: () => gt('Category was updated successfully.'),
          })(args, api);
        },
      }),
      deleteCategory: build.mutation<unknown, {id: string | number}>({
        query: ({id}) => ({
          url: `${KB_CATEGORIES_API_URL}${id}/`,
          method: 'DELETE',
        }),
        invalidatesTags: () => [{type: CATEGORY_TAG, id: 'list'}],
        onQueryStarted: (args, api) => {
          showLoadingBar(args, api);
          showMessages({
            onError: () => gt('Unable to delete category.'),
            onSuccess: () => gt('The category has been removed.'),
          })(args, api);
        },
      }),
      articles: build.query<
        ListResponseType<KBArticleSerializedType>,
        ArticleLocationParamsType
      >({
        query: (params = {}) => ({
          url: KB_ARTICLES_API_URL,
          params: {page: '1', ...params},
        }),
        providesTags: providesTagsByURL(ARTICLES_TAG),
        onQueryStarted: (args, api) => {
          showLoadingBar(args, api);
          showMessages({
            onError: () => gt('Unable to load articles.'),
          })(args, api);
        },
      }),
      addArticle: build.mutation<unknown, CreateNewArticleProps>({
        query: (body) => ({
          url: KB_ARTICLES_API_URL,
          method: 'POST',
          body,
        }),
        invalidatesTags: () => [{type: ARTICLES_TAG, id: 'list'}],
        onQueryStarted: (args, api) => {
          showLoadingBar(args, api);
          showMessages({
            onError: () => gt('Unable to add the article.'),
            onSuccess: () => gt('The article was added successfully.'),
          })(args, api);
        },
      }),
      deleteArticle: build.mutation<unknown, {id: string | number}>({
        query: ({id}) => ({
          url: `${KB_ARTICLES_API_URL}${id}/`,
          method: 'DELETE',
        }),
        invalidatesTags: () => [{type: ARTICLES_TAG, id: 'list'}],
        onQueryStarted: (args, api) => {
          showLoadingBar(args, api);
          showMessages({
            onError: () => gt('Unable to remove the article.'),
            onSuccess: () => gt('The article has been removed.'),
          })(args, api);
        },
      }),
      bulkAddArticles: build.mutation<
        UserJobSerializedType & {status: 'completed'},
        BulkAddArticlesParams
      >({
        queryFn: (
          {articles},
          api,
          extraOptions,
          baseQuery
        ): Promise<
          QueryReturnValue<
            UserJobSerializedType & {status: 'completed'},
            FetchBaseQueryError,
            any
          >
        > => {
          return api
            .dispatch(
              userJobDispatcher(
                (
                  baseQuery({
                    url: `${KB_ARTICLES_API_URL}bulk_create/`,
                    method: 'POST',
                    body: {
                      articles,
                    },
                  }) as Promise<
                    QueryReturnValue<
                      {job: UserJobSerializedType},
                      FetchBaseQueryError,
                      any
                    >
                  >
                ).then((response) => {
                  if (response.data) {
                    return response.data;
                  } else {
                    throw response.error;
                  }
                })
              )
            )
            .job.then(
              (job) => ({data: job}),
              (error) => ({error})
            );
        },
        invalidatesTags: () => [{type: ARTICLES_TAG, id: 'list'}],
        onQueryStarted: ({articles, ...args}, api) => {
          showLoadingBar(args, api);
          showMessages({
            onStart: () =>
              ngt(
                'The article is being added to the knowledge base...',
                'The articles are being added to the knowledge base...',
                articles.length
              ),
            onSuccess: () => [
              ngt(
                'The article was added to the knowledge base.',
                'The articles were added to the knowledge base.',
                articles.length
              ),
              {level: 'success'},
            ],
            onError: () =>
              ngt(
                "There was an error and we couldn't add the article to the knowledge base.",
                "There was an error and we couldn't add the articles to the knowledge base.",
                articles.length
              ),
          })(args, api);
        },
      }),
      duplicateKnowledgebase: build.mutation<
        KnowledgebaseSerializedType,
        {name: string; url: string}
      >({
        query: (body) => ({
          url: KB_API_URL_COPY,
          method: 'POST',
          body,
        }),
        onQueryStarted: (args, api) => {
          showLoadingBar(args, api);
          showMessages({
            onError: () => gt('Unable to duplicate'),
          })(args, api);
        },
        invalidatesTags: () => [{type: KB_TAG, id: 'list'}],
      }),
      addTheme: build.mutation<
        KBThemeSerializedType,
        Omit<KBThemeSerializedType, 'url' | 'content_type'>
      >({
        query: (body) => ({
          url: KB_THEMES_API_URL,
          method: 'POST',
          body,
        }),
        invalidatesTags: () => [{type: THEMES_TAG, id: 'list'}],
        onQueryStarted: (args, api) => {
          showLoadingBar(args, api);
          showMessages({
            onError: () => gt('Unable to add the theme.'),
            onSuccess: () => gt('The theme was added successfully.'),
          })(args, api);
        },
      }),
      updateTheme: build.mutation<
        KBThemeSerializedType,
        {url: string} & Partial<
          Omit<KBThemeSerializedType, 'url' | 'content_type'>
        >
      >({
        query: ({url, ...body}) => ({
          url,
          method: 'PATCH',
          body,
        }),
        invalidatesTags: (result, error, {url}) => [
          {
            type: THEMES_TAG,
            id: url,
          },
        ],
        onQueryStarted: (args, api) => {
          showLoadingBar(args, api);
          showMessages({
            onError: () => gt('Unable to update the theme.'),
            onSuccess: () => gt('Theme was updated successfully.'),
          })(args, api);
        },
      }),
      deleteTheme: build.mutation<unknown, string>({
        query: (url) => ({url, method: 'DELETE'}),
        onQueryStarted: showMessages({
          onError: () => gt("Couldn't delete the theme."),
          onSuccess: () => gt('Theme deleted'),
        }),
        invalidatesTags: () => [{type: THEMES_TAG, id: 'list'}],
      }),

      themes: build.query<
        ListResponseType<KBThemeSerializedType>,
        KBLocationParamsType
      >({
        query: (params) => ({
          url: KB_THEMES_API_URL,
          params: {page: '1', ...params},
        }),
        providesTags: providesTagsByURL(THEMES_TAG),
        onQueryStarted: (args, api) => {
          showLoadingBar(args, api);
          showMessages({
            onError: () => gt('Unable to load themes.'),
          })(args, api);
        },
      }),

      addBlock: build.mutation<KBBlockSerializedType, KBBlockWriteType>({
        query: (body) => ({
          url: KB_BLOCKS_API_URL,
          method: 'POST',
          body,
        }),
        invalidatesTags: [{type: BLOCKS_TAG, id: 'list'}],
        onQueryStarted: (args, api) => {
          showMessages({
            onError: () => gt('Unable to add the block.'),
            onSuccess: () => gt('The block was added successfully.'),
          })(args, api);
        },
      }),
      updateBlock: build.mutation<
        KBBlockSerializedType,
        {url: string} & Partial<KBBlockWriteType>
      >({
        query: ({url, ...body}) => ({
          url,
          method: 'PATCH',
          body,
        }),
        invalidatesTags: (result, error, {url}) => [
          {
            type: BLOCKS_TAG,
            id: url,
          },
          {
            type: BLOCKS_TAG,
            id: 'list',
          },
        ],
        onQueryStarted: (args, api) => {
          // Example for showing loading and message handling
          showMessages({
            onError: () => gt('Unable to update the block.'),
            onSuccess: () => gt('Block was updated successfully.'),
          })(args, api);
        },
      }),
      bulkDeleteArticles: build.mutation<
        UserJobSerializedType & {status: 'completed'},
        BulkDeleteArticlesParams
      >({
        queryFn: (
          {articles},
          api,
          extraOptions,
          baseQuery
        ): Promise<
          QueryReturnValue<
            UserJobSerializedType & {status: 'completed'},
            FetchBaseQueryError,
            any
          >
        > => {
          return api
            .dispatch(
              userJobDispatcher(
                (
                  baseQuery({
                    url: `${KB_ARTICLES_API_URL}bulk_delete/`,
                    method: 'POST',
                    body: {
                      articles,
                    },
                  }) as Promise<
                    QueryReturnValue<
                      {job: UserJobSerializedType},
                      FetchBaseQueryError,
                      any
                    >
                  >
                ).then((response) => {
                  if (response.data) {
                    return response.data;
                  } else {
                    throw response.error;
                  }
                })
              )
            )
            .job.then(
              (job) => ({data: job}),
              (error) => ({error})
            );
        },
        invalidatesTags: () => [{type: ARTICLES_TAG, id: 'list'}],
        onQueryStarted: ({articles, ...args}, api) => {
          showLoadingBar(args, api);
          showMessages({
            onStart: () =>
              ngt(
                'The selected document is being removed from the knowledge base...',
                'The selected documents are being removed from the knowledge base...',
                articles.length
              ),
            onSuccess: () => [
              ngt(
                'The selected document has been removed from the knowledge base.',
                'The selected documents have been removed from the knowledge base.',
                articles.length
              ),
              {level: 'success'},
            ],
            onError: () =>
              ngt(
                "There was an error and we couldn't remove the selected document from the knowledge base.",
                "There was an error and we couldn't remove the selected documents from the knowledge base.",
                articles.length
              ),
          })(args, api);
        },
      }),
    }),
  });

// https://redux-toolkit.js.org/rtk-query/usage/queries#hook-types
export const {
  useKnowledgeBasesQuery: useKnowledgeBases,
  useKnowledgeBaseQuery: useKnowledgeBase,
  useDuplicateKnowledgebaseMutation: useDuplicateKnowledgebase,
  useCategoriesQuery: useCategories,
  useAddCategoryMutation: useAddCategory,
  useUpdateCategoryMutation: useUpdateCategory,
  useArticlesQuery: useArticles,
  useAddArticleMutation: useAddArticle,
  useDeleteArticleMutation: useDeleteArticle,
  useBulkAddArticlesMutation: useBulkAddArticles,
  useThemesQuery: useThemes,
  useAddThemeMutation: useAddTheme,
  useUpdateThemeMutation: useUpdateTheme,
  useDeleteThemeMutation: useDeleteTheme,
  useDeleteKnowledgeBaseMutation: useDeleteKnowledgeBase,
  useDeleteCategoryMutation: useDeleteCategory,
  useUpdateKnowledgeBaseMutation: useUpdateKnowledgeBase,
  useAddBlockMutation: useAddBlock,
  useUpdateBlockMutation: useUpdateBlock,
  useBulkDeleteArticlesMutation: useBulkDeleteArticles,
} = extendedAPI;

// https://redux-toolkit.js.org/rtk-query/usage/queries#hook-types
export const {useLazyQuery: useLazyArticles, useQueryState: useArticleState} =
  extendedAPI.endpoints.articles;

export const {
  useLazyQuery: useLazyCategories,
  useQueryState: useCategoriesState,
} = extendedAPI.endpoints.categories;

export const convertThemeLocationParamsToFetchParams = (
  query: KBThemeLocationParamsType
): KBThemeFetchParamsType => {
  const filters = query.filter != null ? query.filter.split(',') : [];
  return {
    page: query.page != null ? query.page : '1',
    page_size:
      query.page_size != null ? query.page_size : `${DEFAULT_PAGE_SIZE}`,
    search: query.search,
    ordering: query.ordering,
    private:
      filters.indexOf('private') > -1
        ? 'true'
        : filters.indexOf('public') > -1
        ? 'false'
        : undefined,
  };
};

export const convertKnowledgebaseLocationParamsToFetchParams = (
  query: KBLocationParamsType
): KBFetchParamsType => ({
  ...query,
  page: query.page != null ? query.page : '1',
  page_size: query.page_size != null ? query.page_size : `${DEFAULT_PAGE_SIZE}`,
});
